import {
  DetailedHTMLProps,
  HTMLAttributes,
  MouseEventHandler,
  useRef,
} from 'react';
import useClickOutside from './useClickOutside';
import useDisclosure from './useDisclosure';
import useEscape from './useEscape';

interface UseAriaDisclosureOptions {
  defaultIsOpen?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  closeOnOutsideClick?: boolean | 'targetOnly'
  closeOnEscape?: boolean
}

export default function useAriaDisclosure<RefElement extends HTMLElement>(
  targetId: string,
  options?: UseAriaDisclosureOptions,
) {
  const {
    defaultIsOpen, onClick, closeOnOutsideClick, closeOnEscape,
  } = options || {};
  const [isOpen, toggleIsOpen] = useDisclosure(defaultIsOpen);

  const toggleRef = useRef<HTMLButtonElement>(null);
  const targetRef = useRef<RefElement>(null);

  useClickOutside({
    refs: closeOnOutsideClick === 'targetOnly'
      ? targetRef
      : [toggleRef, targetRef],
    unless: !isOpen || !closeOnOutsideClick,
    onClick: toggleIsOpen,
  });

  useEscape({
    unless: !isOpen || !closeOnEscape,
    onPress: toggleIsOpen,
  });

  const toggleProps: DetailedHTMLProps<
  HTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
  > = {
    'aria-controls': targetId,
    'aria-expanded': isOpen,
    ref: toggleRef,
    onClick: (e) => {
      if (onClick) {
        onClick(e);
      }
      toggleIsOpen();
    },
  };

  return {
    isOpen,
    toggleIsOpen,
    toggleProps,
    targetRef,
  } as const;
}
